import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SmartGridComponent, ColDef, GridConfig } from 'app/shared/components/smart-grid';
import { MOCK_ORDERS, MockOrder } from '../mock-data/mock-orders.data';

/**
 * Advanced Grid Example
 * Demonstrates multi-column sorting, custom formatters, and advanced configuration
 */
@Component({
  selector: 'app-advanced-example',
  standalone: true,
  imports: [CommonModule, SmartGridComponent],
  template: `
    <div class="p-6">
      <div class="mb-4">
        <h2 class="text-2xl font-bold mb-2">Ejemplo Avanzado</h2>
        <p class="text-gray-600 mb-2">
          Grid con ordenamiento multi-columna. Haz clic en múltiples columnas
          para ordenar por varios campos simultáneamente.
        </p>
        <div class="flex gap-2 flex-wrap">
          <span class="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-semibold">
            Multi-Column Sorting
          </span>
          <span class="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold">
            Max 3 columnas
          </span>
          <span class="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold">
            Prioridad visible
          </span>
        </div>
      </div>

      <div class="bg-white dark:bg-gray-800 rounded-lg shadow">
        <app-smart-grid
          [data]="orders"
          [columnDefs]="columnDefs"
          [config]="gridConfig">
        </app-smart-grid>
      </div>

      <div class="mt-4 p-4 bg-purple-50 dark:bg-purple-900 rounded">
        <h3 class="font-semibold mb-2">💡 Cómo usar:</h3>
        <ul class="text-sm space-y-1 list-disc list-inside">
          <li>Haz clic en el header de una columna para ordenar</li>
          <li>Haz clic en otra columna para agregar un segundo nivel de ordenamiento</li>
          <li>Los números (1, 2, 3) muestran la prioridad de ordenamiento</li>
          <li>Haz clic 3 veces en una columna para quitar el ordenamiento</li>
          <li>Máximo 3 columnas pueden estar ordenadas simultáneamente</li>
        </ul>
      </div>

      <div class="mt-4 p-4 bg-blue-50 dark:bg-blue-900 rounded">
        <h3 class="font-semibold mb-2">📝 Código de este ejemplo:</h3>
        <pre class="text-xs overflow-x-auto"><code>{{exampleCode}}</code></pre>
      </div>
    </div>
  `,
})
export class AdvancedExampleComponent {
  orders: MockOrder[] = MOCK_ORDERS;

  columnDefs: ColDef<MockOrder>[] = [
    {
      field: 'serial',
      headerName: 'Nro. Documento',
      width: 180,
    },
    {
      field: 'soldTo.name',
      headerName: 'Cliente',
      width: 200,
    },
    {
      field: 'class.name',
      headerName: 'Clase',
      width: 150,
    },
    {
      field: 'soldTo.address.city',
      headerName: 'Ciudad',
      width: 150,
    },
    {
      field: 'total',
      headerName: 'Total',
      width: 120,
      valueFormatter: (value) => `${value.toFixed(2)}`,
    },
    {
      field: 'createdAt',
      headerName: 'Fecha',
      width: 150,
      valueFormatter: (value) => new Date(value).toLocaleDateString(),
    },
  ];

  gridConfig: GridConfig<MockOrder> = {
    pagination: {
      enabled: true,
      pageSize: 25,
    },
    sorting: {
      enabled: true,
      multiColumn: true,        // ✨ Habilitar multi-column sorting
      maxMultiSortColumns: 3,   // Máximo 3 columnas
    },
    styling: {
      maxHeight: '600px',
      stripedRows: true,        // Filas alternadas
    },
  };

  exampleCode = `// Component Code
columnDefs: ColDef<MockOrder>[] = [
  { field: 'soldTo.name', headerName: 'Cliente' },
  { field: 'class.name', headerName: 'Clase' },
  { field: 'soldTo.address.city', headerName: 'Ciudad' },
  { field: 'total', headerName: 'Total',
    valueFormatter: (value) => \` \${value.toFixed(2)}\` },
];

gridConfig: GridConfig<MockOrder> = {
  sorting: {
    enabled: true,
    multiColumn: true,        // ✨ Multi-column sorting
    maxMultiSortColumns: 3    // Máximo 3 columnas
  },
  pagination: { enabled: true, pageSize: 25 },
  styling: {
    maxHeight: '600px',
    stripedRows: true         // Filas alternadas
  }
};

// Template
<app-smart-grid
  [data]="orders"
  [columnDefs]="columnDefs"
  [config]="gridConfig">
</app-smart-grid>

// Ejemplo de uso programático:
onGridReady(event: GridReadyEvent<Order>) {
  // Ordenar por cliente y luego por total
  event.api.setSortState([
    { field: 'soldTo.name', direction: 'asc' },
    { field: 'total', direction: 'desc' }
  ]);
}

// Escuchar cambios de ordenamiento (API mode):
onSortChanged(event: SortChangedEvent<Order>) {
  // Aquí llamarías a tu API con el nuevo estado
}`;
}
